/**
 * Liens Contexte Codex : à partir d'un exercice, retourne les entrées du Codex
 * à propos du compositeur, du style et de la forme (genre) pour afficher des liens cliquables.
 */

import { getComposerStyle } from './tagGenerator'
import { getComposerDisplayName } from './exerciseDisplay'
import { CLASSICAL_GENRES } from '../data/formations'

const STYLE_LABELS = ['Baroque', 'Classique', 'Romantique', 'Moderne']

/** Mapping style → id de la fiche Codex */
const STYLE_TO_CODEX_ID = {
  Baroque: 'note-style-baroque',
  Classique: 'note-style-classique',
  Romantique: 'note-style-romantique',
  Moderne: 'note-style-moderne'
}

/** Genres qui ont une section dédiée dans la fiche note-formes-genres */
const GENRE_SECTIONS_IN_FORMES = new Set([
  'symphonie', 'concerto', 'sonate', 'trio', 'quatuor_cordes', 'lied_melodie',
  'opera', 'prelude', 'nocturne', 'ballade'
])

let dynamicCodexMappings = {
  byTag: {},
  byComposer: {},
  byGenre: {},
  byStyle: {}
}

export function setCodexDynamicMappings(mappings) {
  dynamicCodexMappings = {
    byTag: mappings?.byTag || {},
    byComposer: mappings?.byComposer || {},
    byGenre: mappings?.byGenre || {},
    byStyle: mappings?.byStyle || {}
  }
}

function normalizeMapKey(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, '_')
}

/**
 * Retourne la liste des liens Contexte Codex pour un exercice (compositeur, style, forme).
 * Au plus un lien par type pour garder l'UI lisible.
 * @param {object} exercise - Exercice avec metadata (composer, genre) et autoTags
 * @returns {Array<{ type: 'composer'|'style'|'form', label: string, codexEntryId: string, sectionId?: string }>}
 */
export function getCodexContextLinksForExercise(exercise) {
  const links = []
  const seen = new Set()

  // Style : déduit du compositeur ou des autoTags
  let style = null
  const composerDisplay = getComposerDisplayName(exercise.metadata)
  if (composerDisplay) {
    style = getComposerStyle(composerDisplay)
  }
  if (!style && Array.isArray(exercise.autoTags)) {
    for (const tag of exercise.autoTags) {
      if (typeof tag === 'string' && STYLE_LABELS.includes(tag)) {
        style = tag
        break
      }
    }
  }
  if (style && STYLE_TO_CODEX_ID[style]) {
    links.push({
      type: 'style',
      label: style,
      codexEntryId: STYLE_TO_CODEX_ID[style]
    })
    seen.add('style')
  }

  // Compositeur : lien vers la fiche "Quelques compositeurs"
  const composer = composerDisplay?.trim()
  if (composer && !seen.has('composer')) {
    links.push({
      type: 'composer',
      label: composer,
      codexEntryId: 'note-compositeurs'
    })
    seen.add('composer')
  }

  // Forme / genre : lien vers la fiche "Formes et genres" (optionnellement avec section)
  const genreId = exercise.metadata?.genre
  if (genreId && !seen.has('form')) {
    const genreDef = CLASSICAL_GENRES.find((g) => g.id === genreId)
    const label = genreDef?.label || genreId
    const sectionId = GENRE_SECTIONS_IN_FORMES.has(genreId) ? genreId : undefined
    links.push({
      type: 'form',
      label,
      codexEntryId: 'note-formes-genres',
      ...(sectionId && { sectionId })
    })
    seen.add('form')
  }

  // Mappings dynamiques (prioritaires, éditables via l'admin)
  const mappedLinks = []
  const pushMapped = (type, label, mapValue) => {
    if (!mapValue?.codexEntryId) return
    const key = `${type}:${mapValue.codexEntryId}:${mapValue.sectionId || ''}`
    if (seen.has(key)) return
    seen.add(key)
    mappedLinks.push({
      type,
      label,
      codexEntryId: mapValue.codexEntryId,
      ...(mapValue.sectionId ? { sectionId: mapValue.sectionId } : {})
    })
  }

  if (style) {
    const m = dynamicCodexMappings.byStyle?.[normalizeMapKey(style)]
    pushMapped('style', style, m)
  }
  if (composer) {
    const m = dynamicCodexMappings.byComposer?.[normalizeMapKey(composer)]
    pushMapped('composer', composer, m)
  }
  if (genreId) {
    const m = dynamicCodexMappings.byGenre?.[normalizeMapKey(genreId)]
    const genreDef = CLASSICAL_GENRES.find((g) => g.id === genreId)
    pushMapped('form', genreDef?.label || genreId, m)
  }
  if (Array.isArray(exercise.autoTags)) {
    for (const tag of exercise.autoTags) {
      if (typeof tag !== 'string') continue
      const m = dynamicCodexMappings.byTag?.[normalizeMapKey(tag)]
      pushMapped('tag', tag, m)
    }
  }

  return mappedLinks.length > 0 ? mappedLinks : links
}

/**
 * Construit l'URL du dashboard pour ouvrir le Codex sur une fiche (et optionnellement une section).
 * @param {string} codexEntryId - ID de la fiche
 * @param {string} [sectionId] - ID de section pour scroll (optionnel)
 * @returns {string}
 */
export function getCodexUrlForEntry(codexEntryId, sectionId = null, nodeId = null) {
  const params = new URLSearchParams({ tab: 'codex' })
  if (codexEntryId) params.set('fiche', codexEntryId)
  if (sectionId) params.set('section', sectionId)
  if (nodeId) params.set('node', nodeId)
  return `/student-dashboard?${params.toString()}`
}
